import { Button } from "@faire/slate/components/button";
import { CreditCard } from "@faire/slate-icons/CreditCard";
import { motion, useReducedMotion } from "framer-motion";
import type { Variants } from "framer-motion";

import {
  NET_TERMS_AMOUNT_USD,
  NET_TERMS_CARD_COPY,
  NET_TERMS_COPY,
  NET_TERMS_DISCLAIMER,
  RETAILER_STORE_NAME,
} from "./data";
import type { CelebrationVariantKey } from "./data";
import {
  CONFETTI_PALETTES,
  Confetti,
  EASE_OUT_EXPO,
  SignupStepHeader,
  useCountUp,
} from "./shared";

export interface RevealStageProps {
  variant: CelebrationVariantKey;
}

export const RevealStage: React.FC<RevealStageProps> = ({ variant }) => {
  switch (variant) {
    case "corner-burst-countup":
      return <CornerBurstCountUpReveal />;
    case "money-confetti":
      return <MoneyConfettiReveal />;
    case "milestone-card":
      return <MilestoneCardReveal />;
    case "classic-rain":
    default:
      return <ClassicRainReveal />;
  }
};

const formatUsd = (amount: number): string =>
  `$${amount.toLocaleString("en-US")}`;

const buildEntranceVariants = (
  reduceMotion: boolean
): { container: Variants; item: Variants } => ({
  container: {
    hidden: {},
    show: { transition: { staggerChildren: reduceMotion ? 0 : 0.12 } },
  },
  item: reduceMotion
    ? { hidden: { opacity: 1, y: 0 }, show: { opacity: 1, y: 0 } }
    : {
        hidden: { opacity: 0, y: 16 },
        show: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.5, ease: EASE_OUT_EXPO },
        },
      },
});

/**
 * Shared body for every celebration variant: same title/subtitle, card slot,
 * CTA and disclaimer as the real SELECT_NET_TERMS step today — only the
 * `card` and confetti overlay (passed by the caller) vary per hypothesis.
 * This XP only changes what happens before this step, not the step itself.
 */
const RevealContent: React.FC<{ card: React.ReactNode }> = ({ card }) => {
  const reduceMotion = useReducedMotion() ?? false;
  const { container, item } = buildEntranceVariants(reduceMotion);

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className="flex w-full max-w-[420px] flex-col items-center gap-(--slate-dimensions-8)"
    >
      <motion.div
        variants={item}
        className="flex flex-col gap-(--slate-dimensions-2)"
      >
        <h1 className="type-fs-display-s text-fs-text-primary text-balance">
          {NET_TERMS_COPY.title}
        </h1>
        <p className="type-fs-paragraph text-fs-text-subdued text-balance">
          {NET_TERMS_COPY.subtitle}
        </p>
      </motion.div>
      <motion.div variants={item} className="w-full">
        {card}
      </motion.div>
      <motion.div
        variants={item}
        className="flex w-full flex-col gap-(--slate-dimensions-4)"
      >
        <Button variant="primary">{NET_TERMS_COPY.ctaLabel}</Button>
        <p className="type-fs-label text-fs-text-subdued text-center">
          {NET_TERMS_DISCLAIMER}
        </p>
      </motion.div>
    </motion.div>
  );
};

/** Recreation of today's real net-terms-card (CoreCard): unchanged by this XP. */
const NetTermsCard: React.FC<{ countUp?: boolean }> = ({ countUp = false }) => {
  const countedAmount = useCountUp(NET_TERMS_AMOUNT_USD, 1100, countUp);
  const displayAmount = countUp ? countedAmount : NET_TERMS_AMOUNT_USD;

  return (
    <div className="bg-fs-expressive-surface-orange shadow-[0px_2px_6px_0px_rgba(0,0,0,0.15)] mx-auto flex w-full max-w-[360px] flex-col rounded-(--slate-dimensions-4) p-(--slate-dimensions-6)">
      <span className="type-fs-paragraph-medium text-fs-text-primary">
        {RETAILER_STORE_NAME}
      </span>
      <span className="type-fs-display-l text-fs-text-primary mt-(--slate-dimensions-10)">
        {formatUsd(displayAmount)}
      </span>
      <span className="type-fs-label text-fs-text-subdued mt-(--slate-dimensions-2)">
        {NET_TERMS_CARD_COPY.availableTermsLabel}
      </span>
    </div>
  );
};

const ClassicRainReveal: React.FC = () => (
  <RevealShell
    confetti={<Confetti pattern="rain" colors={CONFETTI_PALETTES.brand} />}
  >
    <RevealContent card={<NetTermsCard />} />
  </RevealShell>
);

const CornerBurstCountUpReveal: React.FC = () => (
  <RevealShell
    confetti={
      <Confetti
        pattern="cornerBurst"
        colors={CONFETTI_PALETTES.brand}
        pieceCount={72}
      />
    }
  >
    <RevealContent card={<NetTermsCard countUp />} />
  </RevealShell>
);

const MoneyConfettiReveal: React.FC = () => (
  <RevealShell
    confetti={<Confetti pattern="rain" colors={CONFETTI_PALETTES.money} />}
  >
    <RevealContent card={<NetTermsCard />} />
  </RevealShell>
);

const MilestoneCardReveal: React.FC = () => {
  const reduceMotion = useReducedMotion() ?? false;

  return (
    <RevealShell>
      <RevealContent card={<MilestoneCard reduceMotion={reduceMotion} />} />
    </RevealShell>
  );
};

const MilestoneCard: React.FC<{ reduceMotion: boolean }> = ({
  reduceMotion,
}) => {
  const countedAmount = useCountUp(NET_TERMS_AMOUNT_USD, 1100, true);

  return (
    <div className="bg-fs-action-surface-default relative flex aspect-[16/10] w-full flex-col justify-between overflow-hidden rounded-(--slate-dimensions-4) p-(--slate-dimensions-6)">
      <div aria-hidden className="absolute inset-0 overflow-hidden">
        <motion.div
          initial={{ x: "-120%" }}
          animate={reduceMotion ? { x: "-120%" } : { x: "220%" }}
          transition={{ duration: 1.3, delay: 0.5, ease: EASE_OUT_EXPO }}
          className="bg-fs-action-surface-inverse/20 absolute inset-y-0 w-1/3 -skew-x-12"
        />
      </div>
      <div className="relative flex items-center justify-between">
        <span className="type-fs-label text-fs-action-text-inverse tracking-[0.2em]">
          FAIRE TERMS
        </span>
        <CreditCard className="text-fs-action-icon-inverse h-(--slate-dimensions-6) w-(--slate-dimensions-6)" />
      </div>
      <div className="relative flex flex-col gap-(--slate-dimensions-1)">
        <span className="type-fs-label text-fs-action-text-inverse opacity-70">
          Available terms
        </span>
        <span className="type-fs-display-m text-fs-action-text-inverse">
          {formatUsd(countedAmount)}
        </span>
      </div>
    </div>
  );
};

const RevealShell: React.FC<{
  children: React.ReactNode;
  confetti?: React.ReactNode;
}> = ({ children, confetti }) => {
  return (
    <div className="bg-fs-surface-primary relative flex min-h-full w-full flex-col overflow-hidden">
      <SignupStepHeader progressPercent={100} />
      {confetti}
      <div className="relative z-10 mx-auto flex w-full max-w-[480px] flex-1 flex-col items-center justify-center px-(--slate-dimensions-6) py-(--slate-dimensions-10) text-center">
        {children}
      </div>
    </div>
  );
};
